import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles, Send, Loader2, ArrowUpRight, ShieldCheck, Check, X } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";

const STATUS_TEXT = { executed: "Eseguita", cancelled: "Annullata", failed: "Non eseguita", stale: "Da riconfermare: il record è cambiato", executing: "In esecuzione…" };

// CONFIRM -> EXECUTE: l'esito mostrato è sempre quello restituito dal server
const ActionCard = ({ a, onDecide, busy }) => (
  <div className="mt-2 rounded-xl border border-line bg-slate-50 p-3" data-testid={`assistant-action-${a.id}`}>
    <pre className="whitespace-pre-wrap font-sans text-sm text-ink" data-testid="assistant-action-preview">{a.preview}</pre>
    {a.status === "pending" ? (
      <div className="mt-3 flex gap-2">
        <Button size="sm" className="gap-1 rounded-full" disabled={busy} onClick={() => onDecide(a, "confirm")} data-testid="assistant-action-confirm"><Check className="h-4 w-4" />Conferma</Button>
        <Button size="sm" variant="outline" className="gap-1 rounded-full" disabled={busy} onClick={() => onDecide(a, "cancel")} data-testid="assistant-action-cancel"><X className="h-4 w-4" />Annulla</Button>
      </div>
    ) : (
      <p className={`mt-2 text-xs font-medium ${a.status === "executed" ? "text-emerald-700" : "text-amber-700"}`} data-testid="assistant-action-status">
        {STATUS_TEXT[a.status] || a.status}{a.result?.text ? ` · ${a.result.text}` : ""}{a.error ? ` · ${a.error}` : ""}
        {a.result?.link && <Link to={a.result.link} className="ml-2 text-brand-600 hover:underline">Apri</Link>}</p>
    )}
  </div>
);

const Sections = ({ sections, onNav }) => sections.filter((s) => s.records.length).map((s, i) => (
  <div key={i} className="mt-2" data-testid={`assistant-section-${i}`}>
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{s.title} · {s.total}</p>
    <ul className="mt-1 space-y-1">{s.records.slice(0, 6).map((r, j) => (
      <li key={j}><Link to={r.link || "#"} onClick={onNav} className="flex items-start gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50" data-testid={`assistant-record-${i}-${j}`}>
        <ArrowUpRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-600" /><span><span className="font-medium text-ink">{r.label}</span>{r.meta && <span className="block text-xs text-slate-500">{r.meta}</span>}</span></Link></li>))}</ul>
  </div>
));

export const AssistantPanel = ({ open, onOpenChange }) => {
  const loc = useLocation();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sid, setSid] = useState(null);
  const [sugg, setSugg] = useState([]);
  const end = useRef(null);
  useEffect(() => { if (open && !sugg.length) api.get("/assistant/suggestions").then((r) => setSugg(r.data.items)).catch(() => {}); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const send = async (q) => {
    const m = (q ?? text).trim();
    if (!m || busy) return;
    setText(""); setBusy(true);
    setMsgs((x) => [...x, { role: "user", text: m }]);
    try {
      const r = await api.post("/assistant/chat", { message: m, session_id: sid, context: { path: loc.pathname } }, { timeout: 70000 });
      setSid(r.data.session_id);
      setMsgs((x) => [...x, { role: "assistant", ...r.data }]);
    } catch (e) { setMsgs((x) => [...x, { role: "assistant", text: errorMessage(e), sections: [] }]); }
    setBusy(false);
  };
  const decide = async (a, d) => {
    setBusy(true);
    try {
      const r = await api.post(`/assistant/actions/${a.id}/${d}`);
      setMsgs((x) => x.map((m) => (m.action?.id === a.id ? { ...m, action: r.data } : m)));
    } catch (e) { setMsgs((x) => [...x, { role: "assistant", text: errorMessage(e), sections: [] }]); }
    setBusy(false);
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col p-0 sm:max-w-md" data-testid="assistant-panel">
        <SheetHeader className="border-b border-line px-5 py-4">
          <SheetTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-brand-600" />ON! Assistant</SheetTitle>
          <SheetDescription className="flex items-center gap-1 text-xs"><ShieldCheck className="h-3.5 w-3.5" />Vede solo i dati del tuo perimetro. Le azioni richiedono sempre conferma.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4" data-testid="assistant-messages">
          {!msgs.length && <div className="flex flex-wrap gap-2" data-testid="assistant-suggestions">{sugg.map((s, i) => (
            <button key={s} type="button" onClick={() => send(s)} className="rounded-full border border-line px-3 py-1.5 text-left text-xs text-slate-600 hover:bg-slate-50" data-testid={`assistant-suggestion-${i}`}>{s}</button>))}</div>}
          {msgs.map((m, i) => m.role === "user"
            ? <div key={i} className="ml-auto max-w-[85%] rounded-2xl bg-ink px-3.5 py-2 text-sm text-white" data-testid={`assistant-user-${i}`}>{m.text}</div>
            : <div key={i} className="max-w-[95%] text-sm text-ink" data-testid={`assistant-reply-${i}`}>
                <p className="whitespace-pre-wrap">{m.text}</p>
                <Sections sections={m.sections || []} onNav={() => onOpenChange(false)} />
                {m.action && <ActionCard a={m.action} onDecide={decide} busy={busy} />}
              </div>)}
          {busy && <p className="flex items-center gap-2 text-xs text-slate-500" data-testid="assistant-loading"><Loader2 className="h-3.5 w-3.5 animate-spin" />Sto verificando i dati…</p>}
          <div ref={end} />
        </div>
        <form className="flex gap-2 border-t border-line p-3" onSubmit={(e) => { e.preventDefault(); send(); }}>
          <Input value={text} maxLength={1000} onChange={(e) => setText(e.target.value)} placeholder="Chiedi o chiedi di fare…" data-testid="assistant-input" />
          <Button type="submit" size="icon" disabled={busy || !text.trim()} data-testid="assistant-send"><Send className="h-4 w-4" /></Button>
        </form>
      </SheetContent>
    </Sheet>
  );
};
